import React, { useState, useEffect, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Calendar,
  Camera,
  Eye,
  Globe,
  Inbox,
  LayoutGrid,
  List,
  MapPin,
  MessageCircle,
  Pencil,
  Plus,
  Search,
  Send,
  Share2,
  Tag,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import CustomPaginator from '../../components/GlobalComponents/CustomPaginator';
import { G, C } from '../../components/GlobalComponents/uiTokens';
import AddEditLeadModal, { SavedLead } from './parts/AddEditLeadModal';
import { LEAD_STATUSES, getStatusStyle, statusLabel } from './parts/leadStatuses';
import { EMPTY_FILTER_OPTIONS, FilterOption, LeadFilterOptions, LeadOrigin, fetchLeadFilterOptions } from './parts/leadOrigin';

const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};
const LABEL: React.CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 600,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: C.sub,
};

type IconCmp = React.ComponentType<{ size?: number; style?: React.CSSProperties; color?: string }>;
const SOURCE_ICONS: Record<string, IconCmp> = {
  facebook: Share2,
  instagram: Camera,
  google: Search,
  referral: Users,
  website: Globe,
  walk_in: MapPin,
  whatsapp: MessageCircle,
};

// ── Types ─────────────────────────────────────────────────────────────────────
interface PersonRef {
  id: string | null;
  email: string | null;
  role: string | null;
}
interface Lead {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  source: string | null;
  status: string | null;
  notes: string | null;
  city: string | null;
  interested_in: string | null;
  agency_id: string | null;
  created_by: PersonRef | null;
  assigned_to: PersonRef | null;
  assigned_by: PersonRef | null;
  assigned_at: string | null;
  created_at: string | null;
  sheet_id?: string | null;
  sheet_import_id?: string | null;
  origin?: LeadOrigin | null;
  custom_fields?: Record<string, string> | null;
}
interface ApiResponse {
  success: boolean;
  data: Lead[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d: string | null) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return d;
  }
};
const AVCOLORS = ['#115550', '#16706a', '#1a8a82', '#2563eb', '#7c3aed', '#db2777', '#ea580c'];
const avc = (s: string | null) => {
  if (!s) return AVCOLORS[0];
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return AVCOLORS[Math.abs(h) % AVCOLORS.length];
};
const ini2 = (e: string | null) => (e ? e.split('@')[0].substring(0, 2).toUpperCase() : '?');

const SELECT_ARROW = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='5'%3E%3Cpath d='M0 0l4.5 5L9 0z' fill='rgba(17,85,80,0.45)'/%3E%3C/svg%3E")`;
const selectBase: React.CSSProperties = {
  height: '33px',
  minWidth: '120px',
  background: 'white',
  border: 'none',
  outline: 'none',
  fontSize: '0.71rem',
  padding: '0 24px 0 9px',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage: SELECT_ARROW,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 8px center',
};

const dateInputStyle = (filled: boolean): React.CSSProperties => ({
  height: '33px',
  padding: '0 9px',
  border: `1px solid ${G.line}`,
  borderRadius: '7.4px',
  fontSize: '0.7rem',
  background: 'white',
  color: filled ? C.teal : '#374151',
  minWidth: '128px',
  outline: 'none',
});
/** Max allowed span between from/to (calendar months). */
const MAX_DATE_RANGE_MONTHS = 6;

const addMonths = (iso: string, months: number): string => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  d.setMonth(d.getMonth() + months);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const monthsBetween = (from: string, to: string): number => {
  if (!from || !to) return 0;
  const a = new Date(from + 'T00:00:00');
  const b = new Date(to + 'T00:00:00');
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return 0;
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + (b.getDate() >= a.getDate() ? 0 : -1);
};


// ─────────────────────────────────────────────────────────────────────────────

const Leads: React.FC = () => {
  const navigate = useNavigate();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tempSearch, setTempSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [selectedLeads, setSelectedLeads] = useState<Lead[] | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('list');

  // ── Origin filters — always by ID, labels are the latest names ────────────
  // ?sheet_id=… / ?sheet_import_id=… / ?campaign=… can come in from links
  // (sheet sync "Open these leads", lead details "All leads from this sheet").
  const [searchParams, setSearchParams] = useSearchParams();
  const [sheetFilter, setSheetFilter] = useState(searchParams.get('sheet_id') ?? '');
  const [syncFilter, setSyncFilter] = useState(searchParams.get('sheet_import_id') ?? '');
  const [campaignFilter, setCampaignFilter] = useState(searchParams.get('campaign') ?? '');
  const [formFilter, setFormFilter] = useState(searchParams.get('form') ?? '');
  const [filterOptions, setFilterOptions] = useState<LeadFilterOptions>(EMPTY_FILTER_OPTIONS);

  useEffect(() => {
    fetchLeadFilterOptions()
      .then(setFilterOptions)
      .catch(() => setFilterOptions(EMPTY_FILTER_OPTIONS)); // filters just stay hidden
  }, []);

  // ── Add / Edit modal state ────────────────────────────────────────────────
  const [modalVisible, setModalVisible] = useState(false);
  const [editingLeadId, setEditingLeadId] = useState<string | null>(null);

  const openAddModal = () => {
    setEditingLeadId(null);
    setModalVisible(true);
  };

  const openEditModal = (id: string) => {
    setEditingLeadId(id);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingLeadId(null);
  };

  const handleLeadSaved = (_lead: SavedLead) => {
    // Refresh list after create / update
    fetchLeads();
    closeModal();
  };

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const p = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(sourceFilter && { source: sourceFilter }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
        ...(sheetFilter && { sheet_id: sheetFilter }),
        ...(syncFilter && { sheet_import_id: syncFilter }),
        ...(campaignFilter && { campaign: campaignFilter }),
        ...(formFilter && { form: formFilter }),
      });
      const res = await apiClient.get<ApiResponse>(`/v1/leads?${p}`);
      if (res.data.success) {
        setLeads(res.data.data ?? []);
        setPagination(res.data.pagination ?? { total: 0, page: 1, limit: 10, pages: 0 });
      } else setError('Failed to load leads');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter, sourceFilter, dateFrom, dateTo, sheetFilter, syncFilter, campaignFilter, formFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleSearch = () => {
    setSearch(tempSearch);
    setPage(1);
  };
  const handleReset = () => {
    setTempSearch('');
    setSearch('');
    setStatusFilter('');
    setSourceFilter('');
    setDateFrom('');
    setDateTo('');
    setSheetFilter('');
    setSyncFilter('');
    setCampaignFilter('');
    setFormFilter('');
    if (searchParams.toString()) setSearchParams({}, { replace: true });
    setPage(1);
  };

  const clearSyncFilter = () => {
    setSyncFilter('');
    const next = new URLSearchParams(searchParams);
    next.delete('sheet_import_id');
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const hasActiveFilters = !!(
    tempSearch || search || statusFilter || sourceFilter || dateFrom || dateTo ||
    sheetFilter || syncFilter || campaignFilter || formFilter
  );

  /** Keep a filter visible even if its option isn't in the list (e.g. came from a link). */
  const withCurrent = (opts: FilterOption[], current: string, fallback: string): FilterOption[] =>
    !current || opts.some((o) => o.value === current) ? opts : [{ value: current, label: fallback, count: 0 }, ...opts];

  // ── Col styles ────────────────────────────────────────────────────────────
  const CH: React.CSSProperties = {
    background: '#fafbfb',
    color: '#9ca3af',
    fontSize: '0.64rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${G.soft}`,
    padding: '10px 13px',
    letterSpacing: '0.5px',
  };
  const CB: React.CSSProperties = {
    padding: '10px 13px',
    borderBottom: `1px solid ${G.softer}`,
  };

  // ── Templates ─────────────────────────────────────────────────────────────
  const nameTpl = (row: Lead) => {
    const c = avc(row.name || row.email);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8.3px' }}>
        <div
          style={{
            width: '29.5px',
            height: '29.5px',
            borderRadius: '50%',
            background: c,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '0.65rem',
            fontWeight: '700',
            boxShadow: `0 2px 6px ${c}55`,
          }}
        >
          {(row.name || row.email || '?').charAt(0).toUpperCase()}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span
            style={{
              color: C.teal,
              fontSize: '0.76rem',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '134px',
            }}
          >
            {row.name || '-'}
          </span>
          <span
            style={{
              fontSize: '0.63rem',
              color: C.muted,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '134px',
            }}
          >
            {row.email || row.phone || '-'}
          </span>
        </div>
      </div>
    );
  };

  const statusTpl = (row: Lead) => {
    const s = getStatusStyle(row.status);
    return (
      <span
        style={{
          padding: '2.8px 8.3px',
          borderRadius: '20px',
          fontSize: '0.63rem',
          fontWeight: '700',
          textTransform: 'capitalize',
          display: 'inline-block',
          background: s.bg,
          color: s.color,
          border: `1px solid ${s.border}`,
        }}
      >
        {statusLabel(row.status)}
      </span>
    );
  };

  const sourceTpl = (row: Lead) => {
    const SrcIcon = SOURCE_ICONS[(row.source || '').toLowerCase()] || Tag;
    const campaign = row.origin?.campaign?.name || row.origin?.campaign?.id;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2.8px' }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3.7px',
          padding: '2.8px 7.4px',
          borderRadius: '6.5px',
          fontSize: '0.63rem',
          fontWeight: '600',
          background: C.limeDim,
          color: '#4a5e00',
          border: `1px solid ${C.limeBorder}`,
          textTransform: 'capitalize',
        }}
      >
        <SrcIcon size={10} />
        {row.source?.replace('_', ' ') || '-'}
      </span>
      {campaign && (
        <span
          title={`Campaign: ${campaign}`}
          style={{ fontSize: '0.6rem', color: C.muted, maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {campaign}
        </span>
      )}
      </div>
    );
  };

  const assigneeTpl = (row: Lead) => {
    const p = row.assigned_to;
    if (!p?.email) return <span style={{ color: C.muted, fontSize: '0.7rem' }}>—</span>;
    const c = avc(p.email);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4.6px' }}>
        <div
          style={{
            width: '20.3px',
            height: '20.3px',
            borderRadius: '50%',
            background: c,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '0.52rem',
            fontWeight: '700',
          }}
        >
          {ini2(p.email)}
        </div>
        <span
          style={{
            fontSize: '0.68rem',
            color: C.sub,
            maxWidth: '92px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {p.email.split('@')[0]}
        </span>
      </div>
    );
  };

  const actionTpl = (row: Lead) => (
    <div style={{ display: 'flex', gap: '2.8px', justifyContent: 'center' }}>
      {[
        {
          icon: Eye,
          title: 'View',
          fn: () => navigate(`/leads/${row.id}/details`),
          hbg: 'rgba(17,85,80,0.08)',
          hcol: C.teal,
          hbor: 'rgba(17,85,80,0.2)',
        },
        {
          icon: Pencil,
          title: 'Edit',
          fn: () => openEditModal(row.id),
          hbg: C.limeDim,
          hcol: '#4a5e00',
          hbor: C.limeBorder,
        },
        {
          icon: UserPlus,
          title: 'Assign',
          fn: () => {},
          hbg: 'rgba(22,163,74,0.1)',
          hcol: '#15803d',
          hbor: 'rgba(22,163,74,0.3)',
        },
      ].map((btn) => (
        <button
          key={btn.title}
          onClick={btn.fn}
          title={btn.title}
          style={{
            width: '25px',
            height: '25px',
            background: 'transparent',
            border: '1px solid transparent',
            borderRadius: '5.5px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: C.sub,
            padding: 0,
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = btn.hbg;
            e.currentTarget.style.borderColor = btn.hbor;
            e.currentTarget.style.color = btn.hcol;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'transparent';
            e.currentTarget.style.color = C.sub;
          }}
        >
          {React.createElement(btn.icon, {
            size: 14,
            strokeWidth: 2,
            style: { flexShrink: 0, display: 'block' },
          })}
        </button>
      ))}
    </div>
  );

  // ── Shared empty state (list + cards) ─────────────────────────────────────
  const emptyState = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '36px 0',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'rgba(17,85,80,0.07)',
          border: '1px solid rgba(17,85,80,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Inbox size={22} color={C.teal} />
      </div>
      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: C.teal }}>No leads found</span>
    </div>
  );

  // ── Cards ─────────────────────────────────────────────────────────────────
  const CardsView = () =>
    leads.length === 0 ? (
      emptyState
    ) : (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(231px,1fr))',
          gap: '11px',
          padding: '14.8px 16.6px',
        }}
      >
        {leads.map((lead) => {
          const s = getStatusStyle(lead.status);
          const c = avc(lead.name || lead.email);
          return (
            <div
              key={lead.id}
              onClick={() => navigate(`/leads/${lead.id}/details`)}
              style={{
                ...SHELL,
                borderRadius: '11px',
                padding: '3.7px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLDivElement;
                el.style.boxShadow = '0 5px 18px rgba(17,85,80,0.10)';
                el.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLDivElement;
                el.style.boxShadow = 'none';
                el.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ ...INNER, borderRadius: '8.3px', padding: '12.9px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '9.2px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
                    <div
                      style={{
                        width: '31.4px',
                        height: '31.4px',
                        borderRadius: '50%',
                        background: c,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '0.69rem',
                        fontWeight: '700',
                      }}
                    >
                      {(lead.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.76rem', fontWeight: '700', color: C.teal }}>
                        {lead.name || '-'}
                      </div>
                      <div style={{ fontSize: '0.63rem', color: C.muted }}>
                        {lead.phone || lead.email || '-'}
                      </div>
                    </div>
                  </div>
                  <span
                    style={{
                      padding: '1.8px 6.5px',
                      borderRadius: '20px',
                      fontSize: '0.58rem',
                      fontWeight: '700',
                      textTransform: 'capitalize',
                      background: s.bg,
                      color: s.color,
                      border: `1px solid ${s.border}`,
                    }}
                  >
                    {statusLabel(lead.status)}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3.7px',
                    fontSize: '0.68rem',
                    color: C.sub,
                    paddingTop: '9.2px',
                    borderTop: `1px solid ${G.soft}`,
                  }}
                >
                  {lead.source && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3.7px' }}>
                      {React.createElement(
                        SOURCE_ICONS[(lead.source || '').toLowerCase()] || Tag,
                        { size: 10, color: '#4a5e00' }
                      )}
                      <span style={{ textTransform: 'capitalize' }}>
                        {lead.source.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                  {lead.interested_in && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3.7px' }}>
                      <Tag size={10} style={{ color: C.muted }} />
                      {lead.interested_in}
                    </div>
                  )}
                  {lead.city && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3.7px' }}>
                      <MapPin size={10} style={{ color: C.muted }} />
                      {lead.city}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '3.7px' }}>
                    <Calendar size={10} style={{ color: C.muted }} />
                    {fmtDate(lead.created_at)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );

  if (loading && leads.length === 0)
    return (
      <div style={SHELL}>
        <div
          style={{
            ...INNER,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '314px',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <ProgressSpinner
              style={{ width: '37px', height: '37px' }}
              strokeWidth="3"
              animationDuration=".8s"
            />
            <p style={{ marginTop: '9.2px', color: C.teal, fontSize: '0.81rem', fontWeight: '500' }}>
              Loading leads…
            </p>
          </div>
        </div>
      </div>
    );

  const hasSel = !!selectedLeads && selectedLeads.length > 0;
  const bulkBtn = (bg: string, border: string, color: string): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3.7px',
    padding: '4.6px 10.1px',
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: '6.5px',
    color,
    fontSize: '0.67rem',
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  });

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        .leads-dt .p-datatable-thead > tr > th { background:#fafbfb !important; color:#9ca3af !important; font-size:0.64rem !important; font-weight:700 !important; text-transform:uppercase !important; letter-spacing:0.5px !important; border-bottom:1px solid ${G.soft} !important; padding:10px 13px !important; }
        .leads-dt .p-datatable-tbody > tr { transition:background 0.15s ease; }
        .leads-dt .p-datatable-tbody > tr:hover { background:rgba(17,85,80,0.03) !important; }
        .leads-dt .p-datatable-tbody > tr > td { border-bottom:1px solid ${G.softer} !important; padding:10px 13px !important; }
        .leads-dt .p-datatable-tbody > tr:last-child > td { border-bottom:none !important; }
        .leads-dt .p-checkbox .p-checkbox-box.p-highlight { background:#115550 !important; border-color:#115550 !important; }
        .leads-dt .p-checkbox .p-checkbox-box:hover { border-color:#c8e94b !important; }
        .leads-dt .p-datatable-emptymessage > td { padding:0 !important; text-align:center !important; border-bottom:none !important; }
        .lds-sel:focus { outline:none; }
        .lds-inp-light::placeholder { color:rgba(17,85,80,0.35); }
        .lds-sel-light { color-scheme: light; }
        .lds-search-box:focus-within { border-color: rgba(17,85,80,0.35) !important; }
        .lds-date:focus { border-color: rgba(17,85,80,0.35) !important; outline: none; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        {error && (
          <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.79rem' }} />
        )}

        {/* ── Header card ───────────────────────────────────────────────── */}
        <div style={SHELL}>
          <div
            style={{
              ...INNER,
              padding: '12px 16.6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '9.2px',
            }}
          >
            <div>
              <div style={LABEL}>Leads</div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '9.2px', marginTop: '3.7px' }}
              >
                <h1
                  style={{
                    margin: 0,
                    fontSize: '1.01rem',
                    fontWeight: 700,
                    color: '#111827',
                    letterSpacing: '-0.2px',
                  }}
                >
                  All Leads
                </h1>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3.7px',
                    padding: '1.8px 8.3px',
                    borderRadius: '20px',
                    background: 'rgba(17,85,80,0.07)',
                    border: '1px solid rgba(17,85,80,0.18)',
                    color: C.tealMid,
                    fontSize: '0.63rem',
                    fontWeight: 600,
                  }}
                >
                  <Users size={10} />
                  {pagination.total} total
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
              <div
                style={{
                  display: 'flex',
                  gap: '1.8px',
                  background: G.shellBg,
                  borderRadius: '7.4px',
                  padding: '2.8px',
                  border: `1px solid ${G.line}`,
                }}
              >
                {(
                  [
                    { m: 'list', i: List, l: 'List' },
                    { m: 'cards', i: LayoutGrid, l: 'Cards' },
                  ] as const
                ).map((v) => (
                  <button
                    key={v.m}
                    onClick={() => setViewMode(v.m)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3.7px',
                      padding: '3.7px 9.2px',
                      borderRadius: '5.5px',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      background: viewMode === v.m ? C.teal : 'transparent',
                      color: viewMode === v.m ? 'white' : 'rgba(17,85,80,0.55)',
                      border: viewMode === v.m ? `1px solid ${C.teal}` : '1px solid transparent',
                    }}
                  >
                    <v.i size={10} />
                    {v.l}
                  </button>
                ))}
              </div>
              <button
                onClick={openAddModal}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4.6px',
                  padding: '6.5px 13.8px',
                  background: 'rgb(17,85,80)',
                  border: 'none',
                  borderRadius: '7.4px',
                  color: 'white',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(17,85,80,0.25)',
                  transition: 'all 0.2s',
                  letterSpacing: '0.2px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(17,85,80,0.4)';
                  e.currentTarget.style.background = 'rgb(22,112,106)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(17,85,80,0.25)';
                  e.currentTarget.style.background = 'rgb(17,85,80)';
                }}
              >
                <Plus size={11} /> NEW LEAD
              </button>
            </div>
          </div>
        </div>

        {/* ── Filter card ───────────────────────────────────────────────── */}
        <div style={SHELL}>
          <div
            style={{
              ...INNER,
              padding: '9.2px 11px',
              display: 'flex',
              alignItems: 'center',
              gap: '7.4px',
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'flex',
                border: `1px solid ${G.line}`,
                borderRadius: '7.4px',
                overflow: 'hidden',
                background: 'white',
              }}
            >
              <select
                className="lds-sel lds-sel-light"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                style={{ ...selectBase, color: statusFilter ? C.teal : '#374151' }}
              >
                <option value="">All Status</option>
                {LEAD_STATUSES.map((st) => (
                  <option key={st.value} value={st.value}>
                    {st.label}
                  </option>
                ))}
              </select>
              <div style={{ width: 1, background: G.line }} />
              <select
                className="lds-sel lds-sel-light"
                value={sourceFilter}
                onChange={(e) => {
                  setSourceFilter(e.target.value);
                  setPage(1);
                }}
                style={{ ...selectBase, color: sourceFilter ? C.teal : '#374151' }}
              >
                <option value="">All Sources</option>
                {['facebook', 'instagram', 'google', 'referral', 'website', 'walk_in', 'whatsapp'].map(
                  (v) => (
                    <option key={v} value={v}>
                      {v.replace('_', ' ').charAt(0).toUpperCase() + v.replace('_', ' ').slice(1)}
                    </option>
                  )
                )}
              </select>
            </div>

            {/* Origin: sheet / campaign / form — values are IDs, labels are latest names */}
            {(filterOptions.sheets.length > 0 || filterOptions.campaigns.length > 0 || filterOptions.forms.length > 0 ||
              sheetFilter || campaignFilter || formFilter) && (
              <div
                style={{
                  display: 'flex',
                  border: `1px solid ${G.line}`,
                  borderRadius: '7.4px',
                  overflow: 'hidden',
                  background: 'white',
                }}
              >
                {(filterOptions.sheets.length > 0 || sheetFilter) && (
                  <select
                    className="lds-sel lds-sel-light"
                    value={sheetFilter}
                    aria-label="Sheet"
                    title="Every lead ever pushed from this Google Sheet"
                    onChange={(e) => {
                      setSheetFilter(e.target.value);
                      setPage(1);
                    }}
                    style={{ ...selectBase, maxWidth: '190px', color: sheetFilter ? C.teal : '#374151' }}
                  >
                    <option value="">All Sheets</option>
                    {withCurrent(filterOptions.sheets, sheetFilter, 'Selected sheet').map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                        {o.count ? ` (${o.count})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {(filterOptions.campaigns.length > 0 || campaignFilter) && (
                  <>
                    <div style={{ width: 1, background: G.line }} />
                    <select
                      className="lds-sel lds-sel-light"
                      value={campaignFilter}
                      aria-label="Campaign"
                      onChange={(e) => {
                        setCampaignFilter(e.target.value);
                        setPage(1);
                      }}
                      style={{ ...selectBase, maxWidth: '190px', color: campaignFilter ? C.teal : '#374151' }}
                    >
                      <option value="">All Campaigns</option>
                      {withCurrent(filterOptions.campaigns, campaignFilter, 'Selected campaign').map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                          {o.count ? ` (${o.count})` : ''}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                {(filterOptions.forms.length > 0 || formFilter) && (
                  <>
                    <div style={{ width: 1, background: G.line }} />
                    <select
                      className="lds-sel lds-sel-light"
                      value={formFilter}
                      aria-label="Form"
                      onChange={(e) => {
                        setFormFilter(e.target.value);
                        setPage(1);
                      }}
                      style={{ ...selectBase, maxWidth: '170px', color: formFilter ? C.teal : '#374151' }}
                    >
                      <option value="">All Forms</option>
                      {withCurrent(filterOptions.forms, formFilter, 'Selected form').map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                          {o.count ? ` (${o.count})` : ''}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            )}

            {/* One specific sync (from Sheet Sync → "Open these leads") */}
            {syncFilter && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  height: '33px',
                  padding: '0 6px 0 10px',
                  borderRadius: '7.4px',
                  background: C.limeDim,
                  border: `1px solid ${C.limeBorder}`,
                  color: '#4a5e00',
                  fontSize: '0.66rem',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                One sync only
                <button
                  onClick={clearSyncFilter}
                  title="Show leads from all syncs"
                  aria-label="Clear sync filter"
                  style={{ display: 'flex', background: 'none', border: 'none', padding: '2px', cursor: 'pointer', color: '#4a5e00' }}
                >
                  <X size={11} />
                </button>
              </span>
            )}

            {/* Date range */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5.5px',
                flexWrap: 'wrap',
              }}
            >
              <Calendar size={12} color={C.muted} style={{ flexShrink: 0 }} />
              <input
                className="lds-date"
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  const v = e.target.value;
                  setDateFrom(v);
                  if (v && dateTo) {
                    const maxTo = addMonths(v, MAX_DATE_RANGE_MONTHS);
                    if (dateTo > maxTo) setDateTo(maxTo);
                    else if (dateTo < v) setDateTo(v);
                  }
                  setPage(1);
                }}
                min={dateTo ? addMonths(dateTo, -MAX_DATE_RANGE_MONTHS) : undefined}
                max={dateTo || undefined}
                title="From date (max 6 months range)"
                aria-label="Date from"
                style={dateInputStyle(!!dateFrom)}
              />
              <span style={{ fontSize: '0.65rem', color: C.muted, fontWeight: 600 }}>to</span>
              <input
                className="lds-date"
                type="date"
                value={dateTo}
                onChange={(e) => {
                  const v = e.target.value;
                  setDateTo(v);
                  if (v && dateFrom) {
                    const minFrom = addMonths(v, -MAX_DATE_RANGE_MONTHS);
                    if (dateFrom < minFrom) setDateFrom(minFrom);
                    else if (dateFrom > v) setDateFrom(v);
                  }
                  setPage(1);
                }}
                min={dateFrom || undefined}
                max={dateFrom ? addMonths(dateFrom, MAX_DATE_RANGE_MONTHS) : undefined}
                title="To date (max 6 months range)"
                aria-label="Date to"
                style={dateInputStyle(!!dateTo)}
              />
            </div>

            <div
              className="lds-search-box"
              style={{
                flex: '1 1 166px',
                minWidth: '148px',
                display: 'flex',
                alignItems: 'center',
                background: 'white',
                border: `1px solid ${G.line}`,
                borderRadius: '7.4px',
                height: '33px',
                padding: '0 9.2px',
                gap: '6.5px',
                transition: 'border-color 0.2s',
              }}
            >
              <Search size={11} style={{ color: C.muted }} />
              <input
                className="lds-inp-light"
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search name, email, phone…"
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: C.teal,
                  fontSize: '0.71rem',
                }}
              />
            </div>

            <button
              onClick={handleSearch}
              style={{
                height: '33px',
                padding: '0 13px',
                background: C.lime,
                border: 'none',
                borderRadius: '7.4px',
                color: C.teal,
                fontSize: '0.68rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '3.7px',
                boxShadow: '0 2px 7px rgba(200,233,75,0.3)',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 3px 10px rgba(200,233,75,0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 7px rgba(200,233,75,0.3)';
              }}
            >
              <Search size={10} /> SEARCH
            </button>

            {hasActiveFilters && (
              <button
                onClick={handleReset}
                style={{
                  height: '33px',
                  padding: '0 10.1px',
                  background: G.shellBg,
                  border: `1px solid ${G.line}`,
                  borderRadius: '7.4px',
                  color: C.sub,
                  fontSize: '0.66rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2.8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(17,85,80,0.08)';
                  e.currentTarget.style.color = C.teal;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = G.shellBg;
                  e.currentTarget.style.color = C.sub;
                }}
              >
                <X size={10} /> RESET
              </button>
            )}
          </div>
        </div>

        {/* ── Table card ────────────────────────────────────────────────── */}
        <div style={SHELL}>
          <div style={{ ...INNER, overflow: 'hidden' }}>
            {/* Toolbar */}
            <div
              style={{
                padding: '11px 16.6px',
                borderBottom: `1px solid ${G.soft}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '9.2px',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
                <div
                  style={{
                    width: '3px',
                    height: '14.8px',
                    background: `linear-gradient(180deg,${C.lime},#8aaa00)`,
                    borderRadius: '3px',
                  }}
                />
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: C.teal }}>
                  Leads Summary
                </span>
                {hasSel && (
                  <span
                    style={{
                      padding: '1.8px 6.5px',
                      borderRadius: '20px',
                      fontSize: '0.61rem',
                      fontWeight: 700,
                      background: C.limeDim,
                      color: '#4a5e00',
                      border: `1px solid ${C.limeBorder}`,
                    }}
                  >
                    {selectedLeads!.length} selected
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '6.5px', alignItems: 'center', flexWrap: 'wrap' }}>
                {hasSel && (
                  <>
                    <button style={bulkBtn('rgba(22,163,74,0.1)', 'rgba(22,163,74,0.3)', '#15803d')}>
                      <UserPlus size={11} /> ASSIGN ({selectedLeads!.length})
                    </button>
                    <button style={bulkBtn(C.limeDim, C.limeBorder, '#4a5e00')}>
                      <Send size={11} /> FOLLOW UP ({selectedLeads!.length})
                    </button>
                    <button style={bulkBtn('rgba(220,38,38,0.08)', 'rgba(220,38,38,0.25)', '#dc2626')}>
                      <Trash2 size={11} /> DELETE ({selectedLeads!.length})
                    </button>
                  </>
                )}
              </div>
            </div>

            {viewMode === 'list' ? (
              <div style={{ overflowX: 'auto' }}>
                <DataTable
                  value={leads}
                  selection={selectedLeads}
                  onSelectionChange={(e: any) => setSelectedLeads(e.value)}
                  dataKey="id"
                  loading={loading}
                  emptyMessage={emptyState}
                  className="leads-dt"
                  style={{ fontSize: '0.76rem' }}
                >
                  <Column
                    selectionMode="multiple"
                    headerStyle={{ ...CH, width: '2.8rem' }}
                    bodyStyle={CB}
                  />
                  <Column
                    field="name"
                    header="Lead"
                    body={nameTpl}
                    headerStyle={{ ...CH, minWidth: '166px' }}
                    bodyStyle={CB}
                  />
                  <Column
                    field="phone"
                    header="Phone"
                    body={(r) => (
                      <span style={{ color: C.sub, fontSize: '0.72rem', fontFamily: 'monospace' }}>
                        {r.phone || '-'}
                      </span>
                    )}
                    headerStyle={CH}
                    bodyStyle={CB}
                  />
                  <Column
                    field="status"
                    header="Status"
                    body={statusTpl}
                    headerStyle={CH}
                    bodyStyle={CB}
                  />
                  <Column
                    field="source"
                    header="Source"
                    body={sourceTpl}
                    headerStyle={CH}
                    bodyStyle={CB}
                  />
                  <Column
                    field="city"
                    header="City"
                    body={(r) => (
                      <span style={{ color: C.sub, fontSize: '0.72rem' }}>
                        {r.city ? (
                          <>
                            <MapPin size={10} style={{ marginRight: '2.8px', color: C.muted }} />
                            {r.city}
                          </>
                        ) : (
                          '-'
                        )}
                      </span>
                    )}
                    headerStyle={CH}
                    bodyStyle={CB}
                  />
                  <Column
                    field="interested_in"
                    header="Interested In"
                    body={(r) => (
                      <span style={{ color: C.sub, fontSize: '0.72rem' }}>{r.interested_in || '-'}</span>
                    )}
                    headerStyle={CH}
                    bodyStyle={CB}
                  />
                  <Column
                    field="assigned_to"
                    header="Assigned"
                    body={assigneeTpl}
                    headerStyle={{ ...CH, minWidth: '129px' }}
                    bodyStyle={CB}
                  />
                  <Column
                    field="created_at"
                    header="Created"
                    body={(r) => (
                      <span style={{ color: C.sub, fontSize: '0.72rem' }}>
                        {fmtDate(r.created_at)}
                      </span>
                    )}
                    headerStyle={CH}
                    bodyStyle={CB}
                  />
                  <Column
                    header="Actions"
                    body={actionTpl}
                    headerStyle={{ ...CH, width: '7.8rem', textAlign: 'center' }}
                    bodyStyle={{ ...CB, textAlign: 'center' }}
                  />
                </DataTable>
              </div>
            ) : (
              <CardsView />
            )}

            <CustomPaginator
              page={page}
              totalPages={pagination.pages}
              totalRecords={pagination.total}
              pageSize={limit}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setLimit(size);
                setPage(1);
              }}
              pageSizeOptions={[5, 10, 20, 50]}
              showInfoBar
            />
          </div>
        </div>
      </div>

      {/* ── Add / Edit Lead Modal ─────────────────────────────────────────── */}
      <AddEditLeadModal
        visible={modalVisible}
        onHide={closeModal}
        onSaved={handleLeadSaved}
        leadId={editingLeadId}
      />
    </>
  );
};

export default Leads;